import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { cn } from '@module/lib/utils';
import { useAddBookmarkMutation, useDeleteBookmarkMutation } from '@module/queries/bookmark/bookmarkQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import {
  Bookmark,
  ChannelBookmarkOff,
  ChannelBookmarkOn,
  LiveBookmarkOnSW22,
  LiveBookmarkSW22,
} from '@module/svg/icons';
import catchAllErrors from '@module/utils/catchAllErrors';
import { useEffect, useState } from 'react';
import BaseButton from './BaseButton';

interface Props extends React.HTMLProps<HTMLButtonElement> {
  initIsBookmark: boolean;
  userIndex: number;
  fetchErrorCallback: (e: unknown) => void;
  fetchSuccessCallback?: () => void;
  width?: number;
  variant?: 'default' | 'play' | 'channel';
  isTooltip?: boolean;
  className?: string;
}

function BookmarkButton({
  initIsBookmark,
  userIndex,
  fetchErrorCallback,
  fetchSuccessCallback,
  variant = 'default',
  width = 25,
  isTooltip = true,
  className,
}: Props) {
  const pushModal = useModalStore((state) => state.pushModal);
  const [isActiveBookmark, setActiveBookmark] = useState(initIsBookmark);
  const { isLogin, userInfo } = useGetLoginInfoQuery();

  const handleAddBookmarkSuccess = () => {
    fetchSuccessCallback?.();
  };

  const handleBookmarkError = (e: unknown) => {
    fetchErrorCallback(e);
  };
  const { mutate: addBookmark } = useAddBookmarkMutation({
    onSuccess: handleAddBookmarkSuccess,
    onError: (e) => catchAllErrors(e, handleBookmarkError),
  });

  const { mutate: deleteBookmark } = useDeleteBookmarkMutation({
    onError: (e) => catchAllErrors(e, handleBookmarkError),
  });

  const handleBookmark = ({ isBookmark, userIdx }: { isBookmark: boolean; userIdx: number | number[] }) => {
    if (isBookmark && typeof userIdx === 'object') {
      deleteBookmark({ userIdx });
      return;
    }
    if (typeof userIdx === 'number') {
      addBookmark({ userIdx });
    }
  };

  const showAuthModal = () => {
    if (aspCond.if('new-asp')) {
      SettingConfig.aspUtils?.goLogin();
      return;
    }

    pushModal({
      type: 'AUTH_MODAL',
      id: 'BookmarkButton',
    });
  };

  useEffect(() => {
    setActiveBookmark(initIsBookmark);
  }, [initIsBookmark]);

  const active = isLogin && isActiveBookmark;

  return (
    <>
      <BaseButton
        className={cn('flex justify-center text-center', className)}
        tooltip={
          isTooltip
            ? {
                content: '북마크',
                side: 'top',
                className: 'z-[30] bg-colorBg_tooltip',
              }
            : undefined
        }
        size='none'
        variant='ghost'
        onClick={() => {
          if (!isLogin) {
            showAuthModal();
            return;
          }

          if (userIndex === userInfo?.idx) {
            pushModal({
              type: 'CONFIRM_MODAL',
              id: 'useRankingViewModel',
              options: {
                description: '자신을 북마크 할 수는 없습니다.',
              },
            });
            return;
          }

          setActiveBookmark((prevState) => {
            if (!prevState) {
              handleBookmark({ isBookmark: prevState, userIdx: userIndex });
            } else {
              handleBookmark({ isBookmark: prevState, userIdx: [userIndex] });
            }

            return !prevState;
          });
        }}
      >
        {variant === 'default' && (
          <>
            {active && (
              <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_bookmark_s_on.png`} alt='bookmark' width={width} />
            )}
            {!active && <Bookmark style={{ fontSize: 8.2 }} />}
          </>
        )}
        {variant === 'play' && (
          <>
            {active && <LiveBookmarkOnSW22 style={{ fontSize: 18 }} />}
            {!active && <LiveBookmarkSW22 style={{ fontSize: 18 }} />}
          </>
        )}
        {variant === 'channel' && (
          <>
            {active && <ChannelBookmarkOn style={{ fontSize: 28 }} />}
            {!active && <ChannelBookmarkOff />}
          </>
        )}
      </BaseButton>
    </>
  );
}

export default BookmarkButton;
